import React, { useEffect, useState } from "react";

import { getOrderDetails, cancelOrder } from "../../services/orderService";
import { updateTicketStatus } from "../../services/kitchenService";
import { isSalon } from "../../utils/businessType";

import "../../styles/pages/Admin/Orders.css";
import useEscapeClose from "../../hooks/useEscapeClose";

// Canonical flow used for the progress strip. Derived ONLY from the order's
// current status — the database stores no status history, so no timestamps
// are invented here.
const STATUS_FLOW = ["Pending", "Confirmed", "Preparing", "Ready", "Served", "Completed"];

// A salon bill has no kitchen stages: it is rung up, then paid.
const SALON_STATUS_FLOW = ["Pending", "Completed"];

const STATUS_OPTIONS = ["Pending", "Preparing", "Ready", "Served", "Completed", "Cancelled"];

const TYPE_LABELS = {
    "Dine-In": "Dine-in",
    "Takeaway": "Takeaway",
    "Delivery": "Delivery"
};

const money = (v) =>
    `₹${Number(v || 0).toLocaleString("en-IN")}`;

function OrderDetailsModal({ order, payments = [], editable = false, onClose, onUpdated }) {

    // Esc closes this modal (src/hooks/useEscapeClose.js).
    useEscapeClose(onClose);

    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(true);
    const [saving, setSaving] = useState(false);
    const [statusDraft, setStatusDraft] = useState(order.order_status);
    const [currentStatus, setCurrentStatus] = useState(order.order_status);

    useEffect(() => {
        let alive = true;
        setLoadingItems(true);
        getOrderDetails(order.id)
            .then((res) => {
                if (alive && res.data.success) {
                    setItems(res.data.data || []);
                }
            })
            .catch((err) => {
                console.error("Order items error:", err);
                if (alive) setItems([]);
            })
            .finally(() => {
                if (alive) setLoadingItems(false);
            });
        return () => {
            alive = false;
        };
    }, [order.id]);

    const orderPayments = payments.filter((p) => String(p.order_id) === String(order.id));

    // A salon sees a bill (customer, stylist, services), not a kitchen order.
    const salon = isSalon();
    const flow = salon ? SALON_STATUS_FLOW : STATUS_FLOW;
    const reachedIndex = flow.indexOf(currentStatus);
    const stepLabel = (step) =>
        step === "Pending" ? "Created" : salon && step === "Completed" ? "Paid" : step;

    const handleSaveStatus = async () => {
        if (!statusDraft || statusDraft === currentStatus) return;
        setSaving(true);
        try {
            await updateTicketStatus(order.id, statusDraft);
            setCurrentStatus(statusDraft);
            alert("Order status updated.");
            if (onUpdated) onUpdated();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Could not update the order status.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelOrder = async () => {
        if (!window.confirm(`Cancel order #${order.id}? This cannot be undone.`)) return;
        setSaving(true);
        try {
            await cancelOrder(order.id);
            setCurrentStatus("Cancelled");
            alert("Order cancelled successfully.");
            if (onUpdated) onUpdated();
            onClose();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Could not cancel the order.");
        } finally {
            setSaving(false);
        }
    };

    const cancelled = currentStatus === "Cancelled";

    return (
        <div className="orders-modal-overlay" onClick={onClose}>

            <div className="orders-modal" onClick={(e) => e.stopPropagation()}>

                <div className="orders-modal-head">
                    <div>
                        <h2>{salon ? "Bill" : "Order"} #{order.id}</h2>
                        <span className="orders-modal-sub">{order.order_number}</span>
                    </div>
                    <div className="orders-modal-head-right">
                        <span className={`order-badge badge-${(currentStatus || "").toLowerCase()}`}>
                            {currentStatus === "Pending"
                                ? (salon ? "Unpaid" : "New")
                                : salon && currentStatus === "Completed" ? "Paid" : currentStatus}
                        </span>
                        <button className="orders-modal-close" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* Meta */}
                <div className="orders-meta-grid">
                    <div className="orders-meta-item">
                        <label>Date &amp; Time</label>
                        <span>
                            {new Date(order.created_at).toLocaleString([], {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                            })}
                        </span>
                    </div>
                    {/* Every salon bill is stored as a counter "Takeaway" order —
                        an internal detail, not something to show a salon. */}
                    {!salon && (
                        <div className="orders-meta-item">
                            <label>Order Type</label>
                            <span>{TYPE_LABELS[order.order_type] || order.order_type}</span>
                        </div>
                    )}
                    {/* A salon bill has a customer, not a table. */}
                    {salon ? (
                        <div className="orders-meta-item">
                            <label>Customer</label>
                            <span>
                                {order.customer_name
                                    ? `${order.customer_name}${order.customer_mobile ? ` · ${order.customer_mobile}` : ""}`
                                    : "Walk-in"}
                            </span>
                        </div>
                    ) : (
                        <div className="orders-meta-item">
                            <label>Table</label>
                            <span>{order.table_name ? order.table_name : "—"}</span>
                        </div>
                    )}
                    {salon && (
                        <div className="orders-meta-item">
                            <label>Stylist</label>
                            <span>{[...new Set((items || []).map((i) => (i.stylist_name || "").trim()).filter(Boolean))].length > 1
                                ? "Multiple (see services)"
                                : (order.stylist_name || "—")}</span>
                        </div>
                    )}
                    <div className="orders-meta-item">
                        <label>{salon ? "Billed by" : "Staff"}</label>
                        <span>{order.employee_name || "—"}</span>
                    </div>
                    <div className="orders-meta-item">
                        <label>Payment Status</label>
                        <span>
                            <span className={`pay-chip pay-${(order.payment_status || "").toLowerCase()}`}>
                                {order.payment_status}
                            </span>
                        </span>
                    </div>
                </div>

                {/* Progress (current status position only — no fabricated history) */}
                {!cancelled ? (
                    <div className="orders-progress">
                        {flow.map((step, i) => (
                            <React.Fragment key={step}>
                                {i > 0 && (
                                    <span className={`orders-progress-bar${reachedIndex >= i ? " done" : ""}`} />
                                )}
                                <span className={`orders-progress-step${reachedIndex >= i ? " done" : ""}`}>
                                    <span className="orders-progress-dot">
                                        {reachedIndex >= i ? "✓" : ""}
                                    </span>
                                    {stepLabel(step)}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                ) : (
                    <div className="orders-cancelled-banner">This {salon ? "bill" : "order"} was cancelled.</div>
                )}

                {/* Items */}
                <div className="orders-items-box">
                    <h3>{salon ? "Services" : "Items"}</h3>
                    {loadingItems ? (
                        <p className="orders-muted">Loading {salon ? "services" : "items"}…</p>
                    ) : items.length === 0 ? (
                        <p className="orders-muted">No {salon ? "services" : "items"} found for this {salon ? "bill" : "order"}.</p>
                    ) : (
                        <table className="orders-items-table">
                            <thead>
                                <tr>
                                    <th>{salon ? "Service" : "Item"}</th>
                                    <th>Qty</th>
                                    <th>Price</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <span className="orders-item-name">{item.item_name}</span>
                                            {salon && item.stylist_name && (
                                                <span className="orders-item-note">· {item.stylist_name}</span>
                                            )}
                                            {item.notes && (
                                                <span className="orders-item-note">+ {item.notes}</span>
                                            )}
                                        </td>
                                        <td>{Number(item.quantity)}</td>
                                        <td>{money(item.price)}</td>
                                        <td>{money(item.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Totals */}
                <div className="orders-totals">
                    <div className="orders-total-row">
                        <span>Subtotal</span>
                        <span>{money(order.subtotal)}</span>
                    </div>
                    {Number(order.discount) > 0 && (
                        <div className="orders-total-row">
                            <span>Discount</span>
                            <span>- {money(order.discount)}</span>
                        </div>
                    )}
                    <div className="orders-total-row">
                        <span>Tax</span>
                        <span>{money(order.tax)}</span>
                    </div>
                    <div className="orders-total-row grand">
                        <span>Grand Total</span>
                        <span>{money(order.grand_total)}</span>
                    </div>
                </div>

                {order.notes && (
                    <div className="orders-notes-box">
                        <label>{salon ? "Notes" : "Order Notes"}</label>
                        <p>{order.notes}</p>
                    </div>
                )}

                {/* Payment records (method / transaction info when already available) */}
                <div className="orders-payment-box">
                    <h3>Payment</h3>
                    <div className="orders-payment-status">
                        <span className={`pay-chip pay-${(order.payment_status || "").toLowerCase()}`}>
                            {order.payment_status}
                        </span>
                        <span className="orders-payment-amount">{money(order.grand_total)}</span>
                    </div>
                    {orderPayments.length > 0 ? (
                        <table className="orders-payments-table">
                            <thead>
                                <tr>
                                    <th>Payment #</th>
                                    <th>Method</th>
                                    <th>Reference</th>
                                    <th>Amount</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderPayments.map((p) => (
                                    <tr key={p.id}>
                                        <td>{p.payment_number}</td>
                                        <td>{p.payment_method}</td>
                                        <td>{p.reference_number || "—"}</td>
                                        <td>{money(p.amount)}</td>
                                        <td>
                                            {new Date(p.payment_date).toLocaleString([], {
                                                day: "2-digit",
                                                month: "short",
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="orders-muted">No payment recorded yet.</p>
                    )}
                </div>

                {/* Edit controls */}
                {editable && !cancelled && (
                    <div className="orders-edit-bar">
                        <div className="orders-edit-field">
                            <label>Update Status</label>
                            <select
                                value={statusDraft}
                                onChange={(e) => setStatusDraft(e.target.value)}
                                disabled={saving}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s === "Pending" ? "New" : s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            className="orders-save-status"
                            onClick={handleSaveStatus}
                            disabled={saving || statusDraft === currentStatus}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                            className="orders-cancel-order"
                            onClick={handleCancelOrder}
                            disabled={saving}
                        >
                            Cancel Order
                        </button>
                    </div>
                )}

            </div>

        </div>
    );

}

export default OrderDetailsModal;
